import axios from "axios";
import store from './redux/store'; 
import axiosRetry from 'axios-retry';
import { baseUrl } from "./redux/actions/baseUrl";
import { toast } from "react-toastify";
import TokenService from './services/tokenService';
import { logoutUserAction } from './redux/actions/userActions';

const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL || baseUrl;


const getToken = () => {
    return TokenService.getToken();
};

const getHospitalId = () => {
    // const { hospital } = store.getState();
    return JSON.parse(localStorage.getItem('hospital'))?? 1; 
};

// Create an Axios instance
const axiosInstance = axios.create({
    baseURL: BASE_URL,
    headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
    },
    // timeout: 10000, // Set a timeout of 10 seconds
});

// Request interceptor to set headers before each request
axiosInstance.interceptors.request.use(
    (config) => {
        const token = getToken();
        const hospitalId = getHospitalId();

        // Set Authorization header if token is available
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        
        }

        // Set X-Hospital-ID header
        config.headers['X-Hospital-ID'] = hospitalId;

        return config;
    },
    (error) => {
        console.error('Request Error:', error);
        return Promise.reject(error);
    }
);

// Variable to prevent multiple token refreshes
let isRefreshing = false;
let refreshSubscribers = [];

// Function to handle token refresh
const refreshToken = async () => {
    try {
        const refreshTokenValue = TokenService.getRefreshToken();
        
        if (!refreshTokenValue) {
            throw new Error('No refresh token available');
        }

        const refreshAxios = axios.create({
            baseURL: BASE_URL,
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-Hospital-ID": getHospitalId(),
            },
        });

        const response = await refreshAxios.post('/auth/refresh-token', {
            refresh_token: refreshTokenValue
        });

        const { token, refresh_token, expires_in } = response.data;

        // Store the new tokens
        if (expires_in) {
            TokenService.setToken(token, refresh_token, expires_in);
        } else {
            TokenService.setSanctumToken(token);
        }

        console.log('Token refreshed successfully');
        return token;
    } catch (error) {
        console.error('Token refresh failed:', error);
        throw error;
    }
};

// Function to subscribe to token refresh
const subscribeTokenRefresh = (cb) => {
    refreshSubscribers.push(cb);
};

// Function to notify all subscribers with the new token
const onRefreshed = (token) => {
    refreshSubscribers.map((cb) => cb(token));
};

// Function to handle logout and redirect
const handleLogout = () => {
    try {
        // Clear tokens
        TokenService.clearTokens();
        
        // Dispatch logout action
        store.dispatch(logoutUserAction());
    
        window.location.href = '/login';
    } catch (error) {
        console.error('Error during logout:', error);
        window.location.href = '/login';
    }
};

// Response interceptor to handle errors and responses
axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        // First check if we have a response at all
        if (!error.response) {
            // Handle network errors or other cases where no response was received
            toast.error('Network error. Please check your connection.');
            console.error('Network Error:', error.message);
            return Promise.reject(error);
        }

        const { config, response: { status } } = error;
        const originalRequest = config;

        // Handle 401 Unauthorized errors
        if (status === 401 && !originalRequest._retry) {
            // Skip refresh for auth endpoints to avoid infinite loops
            if (originalRequest.url?.includes('/auth/')) {
                console.log('Auth endpoint failed, redirecting to login');
                handleLogout();
                return Promise.reject(error);
            }

            if (isRefreshing) {
                // If already refreshing, queue this request
                return new Promise((resolve) => {
                    subscribeTokenRefresh((token) => {
                        originalRequest.headers['Authorization'] = 'Bearer ' + token;
                        resolve(axiosInstance(originalRequest));
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            return new Promise(async (resolve, reject) => {
                try {
                    const newToken = await refreshToken();
                    
                    // Update the default headers
                    axiosInstance.defaults.headers['Authorization'] = 'Bearer ' + newToken;
                    originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
                    
                    // Notify all queued requests
                    onRefreshed(newToken);
                    
                    // Retry the original request
                    resolve(axiosInstance(originalRequest));
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);
                    
                    // Notify queued requests of failure
                    refreshSubscribers.forEach(cb => cb(null));
                    
                    // Handle logout and redirect
                    handleLogout();
                    
                    reject(refreshError);
                } finally {
                    isRefreshing = false;
                    refreshSubscribers = [];
                }
            });
        }

        // Handle different error status codes
        if (status >= 500) {
            toast.error('An error occurred');
            console.error('Server Error:', error.response.data);
        } else if (status >= 400) {
            // You might want to handle specific 4xx errors differently
            console.error('Client Error:', error.response.data);
            // toast.error(error.response.data.message || 'An error occurred');
        }
        return Promise.reject(error);
    }
);

// Configure retry logic using axios-retry
axiosRetry(axiosInstance, {
    retries: 0, // Number of retry attempts
    retryDelay: (retryCount) => {
        return retryCount * 1000; // Delay between retries (in milliseconds)
    },
    retryCondition: (error) => {
        // Retry on network errors or 5xx status codes
        return (
            axiosRetry.isNetworkError(error) ||
            axiosRetry.isRetryableError(error) ||
            error.response?.status >= 500
        );
    },
});

// Export the Axios instance
export default axiosInstance;